/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, Hidden, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

// TODO get rid of circular dependency: https://coursera.atlassian.net/browse/CPM-2491
import EstimatedGraduationCardWrapper from 'bundles/degree-home/components/v3-dpm/components/EstimatedGraduationDate/EstimatedGraduationCard';
import FutureTerms from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/FutureTerms';
import InProgressTerms from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/InProgressTerms';
import type { UseCurriculum_EstimatedAcademicTermFragment as EstimatedAcademicTerm } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import { now } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';
import {
  getFutureTermPlans,
  getInProgressTermPlans,
  getPastTermPlans,
} from 'bundles/degree-home/components/v3-dpm/lib/termsUtils';
import useCurriculum from 'bundles/degree-home/components/v3-dpm/lib/useCurriculum';
import useDegreePlan from 'bundles/degree-home/components/v3-dpm/lib/useDegreePlan';

import _t from 'i18n!nls/degree-home';

import EmptyTerms from './EmptyTerms';
import PastTerms from './PastTerms';
import { useAddTermToPlanningToolDialog } from './context/AddTermsToPlanningToolContext';

const styles = {
  header: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
  `,
  description: css`
    margin: var(--cds-spacing-100) var(--cds-spacing-100) 0;

    ${breakpoints.down('sm')} {
      margin: var(--cds-spacing-300) var(--cds-spacing-200) 0;
      text-align: left;
    }
  `,
  footerButtons: css`
    display: flex;
  `,
  fullWidthMobileButtons: css`
    ${breakpoints.only('xs')} {
      width: 100%;
      max-width: none;
    }
  `,
  pastTermsToggleMobile: css`
    display: flex;
    justify-content: flex-end;
  `,
};

const ToolRenderer: React.VFC = () => {
  const { openAddTermsDialog, additionalTerms } = useAddTermToPlanningToolDialog();
  const degreePlanGql = useDegreePlan();
  const curriculumGql = useCurriculum();

  const [emptyTermPlans, setEmptyTermPlans] = React.useState<EstimatedAcademicTerm[]>([]);
  const [showPastTerms, setShowPastTerms] = React.useState(false);

  const learnerPlan = degreePlanGql.data?.LearnerPlan?.queryPlan?.learnerPlan;
  const terms = curriculumGql?.data?.Degree?.queryById?.academicTermsPaginated?.elements;

  const paginatedTermsOrderMap: Record<string, number> = React.useMemo(() => {
    const paginatedTerms: EstimatedAcademicTerm[] = terms ?? [];

    return paginatedTerms.reduce((currMap, term, index) => ({ ...currMap, [term.estimatedTermId]: index }), {});
  }, [terms]);

  React.useEffect(() => {
    const learnerPlanEstimatedTermIds = learnerPlan?.map((plan) => plan.estimatedTermId) ?? [];

    const emptyTermsMap = additionalTerms;

    for (const id of learnerPlanEstimatedTermIds) {
      if (emptyTermsMap[id]) {
        delete emptyTermsMap[id];
      }
    }

    const emptyTermsList = Object.values(emptyTermsMap);
    const sortedEmptyTerms = emptyTermsList.sort(
      (a, b) => paginatedTermsOrderMap[a.estimatedTermId] - paginatedTermsOrderMap[b.estimatedTermId]
    );

    setEmptyTermPlans(sortedEmptyTerms);
  }, [learnerPlan, additionalTerms, paginatedTermsOrderMap]);

  const currentTime = Number(now());
  const pastTermPlans = getPastTermPlans(currentTime, learnerPlan ?? []);
  const inProgressTermPlans = getInProgressTermPlans(currentTime, learnerPlan ?? []);
  const futureTermPlans = getFutureTermPlans(currentTime, learnerPlan ?? []);

  const handleTogglePastTerms = () => {
    setShowPastTerms(!showPastTerms);
  };

  const showPastTermsButton = () => {
    if (pastTermPlans.length > 0) {
      return (
        <Button variant="ghost" onClick={handleTogglePastTerms}>
          {showPastTerms ? _t('Hide past terms') : _t('Show past terms')}
        </Button>
      );
    }
    return null;
  };

  return (
    <React.Fragment>
      <Hidden smDown>
        <div css={styles.header}>
          <Typography variant="h1semibold">{_t('My Graduation Plan')}</Typography>
          {showPastTermsButton()}
        </div>
      </Hidden>

      <div>
        <Hidden smDown>
          <EstimatedGraduationCardWrapper variant="summary" />
        </Hidden>
        <Typography2 component="p" variant="bodySecondary" css={styles.description}>
          {_t(
            'This is provided for informational purposes only. Schedules are subject to change. Please refer to the upcoming academic term’s course offerings for detailed information.'
          )}
        </Typography2>
        <Hidden mdUp>
          <div css={styles.pastTermsToggleMobile}>{showPastTermsButton()}</div>
        </Hidden>
      </div>
      {showPastTerms && <PastTerms pastTermPlans={pastTermPlans} />}
      <InProgressTerms inProgressTermPlans={inProgressTermPlans} />
      <FutureTerms futureTermPlans={futureTermPlans} />

      <React.Fragment>
        <EmptyTerms emptyTerms={emptyTermPlans} />

        {!!terms?.length && (
          <div css={styles.footerButtons}>
            <Button
              variant="ghost"
              icon={<AddIcon />}
              edgeAlign="start"
              iconPosition="before"
              onClick={() => openAddTermsDialog()}
            >
              {_t('Add academic term')}
            </Button>
          </div>
        )}
      </React.Fragment>
    </React.Fragment>
  );
};

export default ToolRenderer;
